import { Component } from '../../../components/Component.js';
import { observeManyWhenVisible } from '../../../core/utils/dom.js';
import { storage } from '../../../js/utils/storage.js';
import { showFreeAccountModal } from '../../../js/components/FreeAccountModal.js';
import { getCurrentLanguage } from '../../../js/translations.js';
import { getCardAnimationEnabled, getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import {
  fetchAuthedMediaObjectUrl,
  hasRankingCard,
  isVideoCardExt,
  resolveRankingCardUrls
} from '../services/characterCardMedia.js';

const resolveMessagesTotalTooltip = () => {
  const lang = getCurrentLanguage();
  const rankingsT = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
  return rankingsT['rankings.messagesTotalTooltip'] || 'Total number of messages';
};

export class CharacterCardComponent extends Component {
  constructor(element, options = {}) {
    super(element, options);
    this.rankingsState = options.rankingsState || null;
  }

  init() {
    this.element.classList.add('character-card');
    const opts = this.options || {};
    this.character = opts['character'];
    this.onCardClick = opts['onCardClick'] || null;
    this.isPrivateCard = opts.isPrivate === true;
    if (!this.character?.id) return;

    this._onCharacterDetailsUpdated = this.handleCharacterDetailsUpdated.bind(this);
    window.addEventListener('characterDetailsUpdated', this._onCharacterDetailsUpdated);
    this._onCardAnimationPref = () => {
      void this.applyCardMedia();
    };
    this._onNsfwPref = () => {
      void this.applyCardMedia();
    };
    document.addEventListener('cardAnimationPreferenceSelected', this._onCardAnimationPref);
    document.addEventListener('nsfwPreferenceSelected', this._onNsfwPref);

    this.render();
    this.addEventListeners();
    this.initializeFavoriteState();
  }

  handleCharacterDetailsUpdated(event) {
    const { characters } = event.detail || {};
    if (!characters) return;

    const updated = characters.find(char => String(char.id) === String(this.character.id));
    if (!updated) return;

    this.character = { ...this.character, ...updated };
    void this.applyCardMedia();
  }

  render() {
    if (!this.character) {
      this.element.innerHTML = '<div class="card-inner error"><div class="card-front"><div class="error-message">Character data missing</div></div></div>';
      return;
    }

    const { name = 'Unknown Character', rank = '?', id = 'unknown' } = this.character;
    const frontImg = this.character.front_card_preview_url || this.character.image || '/assets/images/kiss.webp';

    this.element.setAttribute('data-id', id);
    this.element.innerHTML = `
        <div class="card-inner">
          <div class="card-front">
            <img data-src="${frontImg}" alt="${name}" class="lazy-load loading card-media" onerror="this.src='/assets/images/kiss.webp'">
            <h3>${name}</h3>
            <div class="card-counts">
              <div class="message-count" title="${resolveMessagesTotalTooltip()}" data-translate-attr="title:rankings.messagesTotalTooltip"><i class="fas fa-comment"></i> ${this.formatPopularity(this.character.pop)}</div>
              <div class="sprites-count" title="Number of sprites"><i class="fas fa-image"></i> ${this.formatPopularity(this.character.sprites)}</div>
            </div>
          </div>
        </div>
        <button class="favorite-btn overlay" title="Add to favorites"><span class="heart-emoji">🤍</span></button>
      `;

    this.cardInner = this.element.querySelector('.card-inner');
    this.setupLazyLoading();
    void this.applyCardMedia();
  }

  async applyCardMedia() {
    if (!this.element || !this.character?.id) return;
    const fallback = this.character.front_card_preview_url || this.character.image || '/assets/images/kiss.webp';
    let src = fallback;
    let isVideo = false;
    let posterUrl = null;
    if (hasRankingCard(this.character, getNsfwEnabled())) {
      const urls = resolveRankingCardUrls(this.character, {
        isPrivate: this.isPrivateCard,
        nsfwEnabled: getNsfwEnabled()
      });
      const useAnimated = getCardAnimationEnabled() && Boolean(urls.videoUrl);
      let nextSrc = useAnimated ? urls.videoUrl : urls.stillUrl;
      let nextPoster = urls.stillUrl;
      const willBeVideo = useAnimated && isVideoCardExt(urls.ext);
      if (urls.needsAuth) {
        const [authedSrc, authedPoster] = await Promise.all([
          nextSrc ? fetchAuthedMediaObjectUrl(nextSrc) : null,
          willBeVideo && nextPoster ? fetchAuthedMediaObjectUrl(nextPoster) : null
        ]);
        nextSrc = authedSrc;
        if (willBeVideo) nextPoster = authedPoster;
      }
      if (nextSrc) {
        src = nextSrc;
        posterUrl = nextPoster;
        isVideo = willBeVideo;
      }
    }

    const front = this.element.querySelector('.card-front');
    if (!front) return;
    const existing = front.querySelector('.card-media');
    if (isVideo) {
      if (existing && existing.tagName === 'VIDEO' && existing.dataset.src === src) return;
      const video = document.createElement('video');
      video.className = 'lazy-load loading card-media';
      video.dataset.src = src;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('aria-label', this.character.name || 'Character card');
      video.preload = 'none';
      if (posterUrl) video.poster = posterUrl;
      if (existing) existing.replaceWith(video);
      else front.prepend(video);
      this.setupLazyLoading();
      return;
    }
    if (existing && existing.tagName === 'IMG') {
      existing.dataset.src = src;
      existing.alt = this.character.name || 'Unknown Character';
      existing.classList.add('lazy-load', 'loading');
      this.setupLazyLoading();
      return;
    }
    const img = document.createElement('img');
    img.className = 'lazy-load loading card-media';
    img.dataset.src = src;
    img.alt = this.character.name || 'Unknown Character';
    img.onerror = () => {
      img.src = '/assets/images/kiss.webp';
    };
    if (existing) existing.replaceWith(img);
    else front.prepend(img);
    this.setupLazyLoading();
  }

  formatPopularity(pop) {
    const num = parseInt(pop);
    if (isNaN(num)) return '0';

    if (num >= 1000000) return Math.floor(num / 100000) / 10 + 'M';
    if (num >= 1000) return Math.floor(num / 100) / 10 + 'k';
    return num.toString();
  }

  setupLazyLoading() {
    this._lazyObserver?.disconnect();
    const media = this.element.querySelector('.card-front .card-media.lazy-load');

    const lazyLoadCallback = (target) => {
      if (target instanceof HTMLVideoElement) {
        target.src = target.dataset.src;
        target.classList.remove('lazy-load', 'loading');
        void target.play?.().catch(() => {});
        return;
      }
      if (!(target instanceof HTMLImageElement)) return;
      target.src = target.dataset.src;
      target.classList.remove('lazy-load', 'loading');
    };

    const initialMedia = [media].filter(Boolean);
    this._lazyObserver = observeManyWhenVisible(initialMedia, lazyLoadCallback,
      { once: true, rootMargin: '0px 0px 100px 0px', threshold: 0.1 });
  }

  addEventListeners() {
    if (!this.element) return;

    const handler = (e) => {
      if (e.target.closest('.option-btn')) return;
      if (e.target.closest('.favorite-btn')) return;
      if (this.onCardClick && this.character?.id) {
        this.onCardClick(this.character);
      }
    };

    this.element['_clickHandler'] = handler;
    this.element.addEventListener('click', handler);

    const favoriteBtn = this.element.querySelector('.favorite-btn');
    if (favoriteBtn) {
      favoriteBtn.addEventListener('click', this.handleFavoriteClick.bind(this));
    }
  }

  getUserId() {
    let userId = storage.get('user_id');
    if (!userId) {
      const userData = storage.get('userData') || {};
      userId = userData.id;
    }
    return userId;
  }

  async handleFavoriteClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const favoriteBtn = e.currentTarget;
    const heartEmoji = favoriteBtn.querySelector('.heart-emoji');
    const characterId = this.character.id;

    const userId = this.getUserId();
    if (!userId) {
      await showFreeAccountModal('rankings-favorite');
      return;
    }

    const originalIsFavorited = this.character.is_favorited;
    const originalHeartText = heartEmoji ? heartEmoji.textContent : '';
    const originalBtnClasses = favoriteBtn.className;

    try {
      favoriteBtn.disabled = true;
      if (heartEmoji) {
        heartEmoji.textContent = '⏳';
      }

      const response = await this.toggleFavorite(userId, characterId);

      if (response.success) {
        const isFavorited = response.is_favorited;

        if (this.rankingsState) {
          this.rankingsState.toggleCharacterFavorite(characterId);
        }

        this.updateFavoriteUI(favoriteBtn, isFavorited);
        this.showFavoriteToast(this.character.name, isFavorited);
      } else {
        console.error('Failed to toggle favorite:', response.error);
        if (heartEmoji) heartEmoji.textContent = originalHeartText;
        favoriteBtn.className = originalBtnClasses;
        this.character.is_favorited = originalIsFavorited;
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      if (heartEmoji) heartEmoji.textContent = originalHeartText;
      favoriteBtn.className = originalBtnClasses;
      this.character.is_favorited = originalIsFavorited;
    } finally {
      favoriteBtn.disabled = false;
    }
  }

  async toggleFavorite(userId, characterId) {
    if (!window.rankingsService) {
      throw new Error('RankingsService is not available');
    }
    return await window.rankingsService.toggleFavoriteCharacter(userId, characterId);
  }

  updateFavoriteUI(favoriteBtn, isFavorited) {
    const heartEmoji = favoriteBtn.querySelector('.heart-emoji');
    if (!heartEmoji) return;

    if (isFavorited) {
      favoriteBtn.classList.add('favorited');
      heartEmoji.textContent = '❤️';
      favoriteBtn.title = 'Remove from favorites';
      heartEmoji.style.animation = 'heartBeat 0.6s ease-in-out';
      setTimeout(() => {
        if (heartEmoji) heartEmoji.style.animation = '';
      }, 600);
    } else {
      favoriteBtn.classList.remove('favorited');
      heartEmoji.textContent = '🤍';
      favoriteBtn.title = 'Add to favorites';
      heartEmoji.style.animation = '';
    }
  }

  async initializeFavoriteState() {
    const favoriteBtn = this.element.querySelector('.favorite-btn');
    if (!favoriteBtn) return;

    const isFavorited = this.rankingsState
      ? this.rankingsState.isCharacterFavorited(this.character.id)
      : false;

    this.updateFavoriteUI(favoriteBtn, isFavorited);
  }

  updateFavoriteDisplay(isFavorited) {
    const favoriteBtn = this.element.querySelector('.favorite-btn');
    if (favoriteBtn) {
      this.updateFavoriteUI(favoriteBtn, isFavorited);
    }
  }

  async showFavoriteToast(characterName, isFavorited) {
    const { showInfo } = await import('../../../js/utils/UnifiedNotificationSystem.js');

    const message = isFavorited
      ? `${characterName} has been added to your favorites`
      : `${characterName} has been removed from your favorites`;

    showInfo(message, {
      icon: isFavorited ? 'heart' : 'heart-broken',
      position: 'top-right',
      duration: 3000,
      replaceExisting: true
    });
  }

  destroy() {
    const handler = this.element && this.element['_clickHandler'];
    if (handler) {
      this.element.removeEventListener('click', handler);
    }

    if (this._onCharacterDetailsUpdated) {
      window.removeEventListener('characterDetailsUpdated', this._onCharacterDetailsUpdated);
    }
    if (this._onCardAnimationPref) {
      document.removeEventListener('cardAnimationPreferenceSelected', this._onCardAnimationPref);
    }
    if (this._onNsfwPref) {
      document.removeEventListener('nsfwPreferenceSelected', this._onNsfwPref);
    }
    this._lazyObserver?.disconnect();
    this._lazyObserver = null;
    const video = this.element?.querySelector('.card-front video.card-media');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    super.destroy();
  }
}
